<template>
  <!-- Multi-term search (like the Explore bar): Enter turns the text into a chip;
       each chip is a query term and a pre-project matches ANY of them. -->
  <v-combobox
    v-model="queryTemp"
    class="pre-projects-search"
    multiple
    clearable
    solo
    @change="onQueryChange"
    @clear="onQueryClear"
  >
    <template v-slot:selection="{ item, index }">
      <BaseChip
        class="pre-projects-search__chip"
        size="sm"
        preset="soft"
        tone="info"
        clearable
        :interactive="false"
        :clear-label="$t('actions.delete')"
        @clear="removeTerm(index)"
      >
        {{ item }}
      </BaseChip>
    </template>
    <template v-slot:prepend-inner>
      <BaseIcon name="search" class="pre-projects-search__icon" />
    </template>
    <template v-slot:label>
      <span class="pre-projects-search__label">{{ searchPlaceholder }}</span>
    </template>
  </v-combobox>
</template>

<script>
import BaseChip from '@/components/base/BaseChip.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { mapActions, mapGetters } from 'vuex';
import { viewport } from '@/util/viewport';

export default {
  name: 'PreProjectsSearchBar',
  components: { BaseChip, BaseIcon },
  data() {
    return {
      // Working copy of the query chips; committed to the store on change.
      queryTemp: [],
    };
  },
  computed: {
    ...mapGetters('preProjects', ['filters']),
    ...mapGetters('preProjectViews', ['activeView']),
    // Placeholder scoped to the active view, like the tenders inbox:
    // "Rechercher un projet dans « <view> »…". Phones: shorter, beside the filters button.
    searchPlaceholder() {
      if (viewport.size === 'phone') return this.$t('page.tenders.views.search-placeholder-short');
      const view = this.activeView;
      const name = view ? (view.nameKey ? this.$t(view.nameKey) : view.name) : '';
      return this.$t('page.pre-projects.filters.search-placeholder', { name });
    },
  },
  methods: {
    ...mapActions('preProjects', ['setFilters']),
    // Each chip is one query term; the backend matches ANY of them
    // (case-insensitive contains on the title).
    onQueryChange() {
      const terms = (this.queryTemp || []).map((term) => String(term).trim()).filter(Boolean);
      this.setFilters({ query: terms });
      this.$emit('change');
    },
    onQueryClear() {
      this.queryTemp = [];
      this.setFilters({ query: [] });
      this.$emit('change');
    },
    removeTerm(index) {
      this.queryTemp.splice(index, 1);
      this.onQueryChange();
    },
  },
  watch: {
    // Keep the chips in sync when the store's query changes from outside this
    // input (view switch applies saved filters, reset, clear-all).
    'filters.query'(query) {
      this.queryTemp = [...(query || [])];
    },
  },
  mounted() {
    this.queryTemp = [...(this.filters.query || [])];
  },
};
</script>

<style lang="scss">
// Multi-term chips search — the Explore bar's combobox styling, at the
// tenders views-panel density.
// Matches the tenders/opportunities views-panel search density: 13px text,
// 16px slate-400 icon at 13px from the edge, 9px icon-to-text gap.
.pre-projects-search {
  &__icon {
    color: $color-slate-400;
    margin: 0 9px 0 5px;
    width: 16px;
  }

  &__label {
    color: $color-text-muted;
    font-size: 13px;
  }

  &__chip {
    margin: 3px;
  }

  &.v-autocomplete {
    border-radius: $border-radius-root;

    &:focus-within {
      .v-input__slot {
        border-color: $color-accent;
        box-shadow: 0 0 0 3px rgba($color-accent-500, 0.1) !important;
      }

      .v-input__prepend-inner .pre-projects-search__icon {
        color: $color-accent;
      }
    }

    .v-input__slot {
      min-height: 40px !important;
      margin: 0;
      padding: 0 12px 0 8px !important;
      background: $color-slate-50 !important;
      border: 1px solid $color-border;
      box-shadow: none !important;
      transition: border 200ms ease, box-shadow 200ms ease !important;
    }

    input {
      font-size: 13px;
    }

    // The placeholder is a Vuetify floating .v-label, not an input placeholder.
    .v-label {
      color: $color-text-muted;
      font-size: 13px;
    }

    .v-text-field__details {
      display: none;
    }

    .v-input__icon--append {
      display: none;
    }
  }
}
</style>
